import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, User, ShoppingCart, Heart, MessageCircle, Menu, X, ChevronDown, BaggageClaimIcon, ShoppingBagIcon, Mail, Phone } from 'lucide-react';
import { cdnAsset } from '../utils/cdn';
const logo = cdnAsset('logo.svg');
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import CurrencySelector from './CurrencySelector';
const mail = cdnAsset('mail.svg');
const call = cdnAsset('call.svg');
const instagram = cdnAsset('instagram.svg');
const pinterest = cdnAsset('pinterest.svg');

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customDropdownOpen, setCustomDropdownOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const { totalItems, openDrawer } = useCart();
  const { user, logout } = useAuth();
  const { items: wishlistItems } = useWishlist();
  const whatsappNumber =
    process.env.REACT_APP_WHATSAPP_NUMBER || "+16463021164";
  const whatsappMessage = encodeURIComponent(
    "Hello Simon! I would like to enquire about this jewelry piece."
  );
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  useEffect(() => {
    const handleScroll = () => {
      const topBarHeight = 40; // Approximate height of blue top bar
      setIsSticky(window.scrollY > topBarHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Categories for CUSTOM dropdown
  const categories = [
    { name: 'All Products', path: '/products' },
    { name: 'Engagement Rings', path: '/product-list/engagement_rings' },
    { name: 'Anniversary Bands', path: '/product-list/finished__annivers' },
    { name: 'Wedding Bands', path: '/product-list/WB' },
    { name: 'Eternity Bands', path: '/product-list/finished__eternity' },
    { name: 'Pendants', path: '/product-list/finished__pendant__classic' },
  ];

  return (
    <header className="bg-white">
      {/* Top Bar - Blue - Responsive */}
      <div className="hidden min-[660px]:block bg-[#0C1322] text-white py-2 sm:py-2">
        <div className="max-w-[1440px] mx-auto px-[48px] lg:px-[96px] flex justify-between items-center text-xs sm:text-sm">
          <div className="relative w-fit font-heading font-medium text-[#ffffff] tracking-[0] leading-[normal] whitespace-nowrap">
            Simon Diamond New York
          </div>
          <nav className="gap-4 inline-flex items-center relative flex-[0_0_auto]" aria-label="Contact information">
            <a
              href="mailto:info@sdnyusa.com"
              className="gap-2 inline-flex items-center relative flex-[0_0_auto] hover:opacity-80 transition-opacity"
              aria-label="Email info@sdnyusa.com"
            >
              <img src={mail} alt="Email" className="w-4 h-4" />
              <span className="relative w-fit mt-[-1.00px] font-light text-[#ffffff] text-base tracking-[0] leading-[normal] whitespace-nowrap">
                info@sdnyusa.com
              </span>
            </a>
            <a
              href="tel:+16463021164"
              className="gap-2 inline-flex items-center relative flex-[0_0_auto] hover:opacity-80 transition-opacity"
              aria-label="Call +1 646 302 1164"
            >
              <img src={call} alt="Phone" className="w-4 h-4" />
              <span className="relative w-fit mt-[-1.00px] font-light text-[#ffffff] text-base tracking-[0] leading-[normal] whitespace-nowrap">
                +1 646 302 1164
              </span>
            </a>
            <a
              href="https://www.instagram.com/sdnyusa?igsh=MW5heWk1NzZxd291OQ=="
              className="gap-2 inline-flex items-center relative flex-[0_0_auto] hover:opacity-80 transition-opacity"
              aria-label="Instagram sdnyusa"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={instagram} alt="Instagram" className="w-4 h-4" />
            </a>
            <a
              href="https://in.pinterest.com/sdnyusa/"
              className="gap-2 inline-flex items-center relative flex-[0_0_auto] hover:opacity-80 transition-opacity"
              aria-label="Pinterest sdnyusa"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={pinterest} alt="Pinterest" className="w-4 h-4" />
            </a>
          </nav>
        </div>
      </div>

      {/* Main Header - Responsive */}
      <div className={`border-b border-gray-200 bg-white transition-all duration-200 ${isSticky ? 'fixed top-0 left-0 right-0 z-40 shadow-md' : 'relative z-10'}`}>
        <div className="max-w-[1440px] mx-auto px-[16px] sm:px-[24px] md:px-[32px] lg:px-[96px] py-4">
          <div className="max-[659px]:flex min-[1200px]:flex hidden items-center justify-between gap-4 md:gap-6">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0">
              <img src={logo} alt="SDNY Logo" className="w-auto h-10 sm:h-11 lg:h-12" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="items-center justify-center flex-1 hidden gap-5 min-[1200px]:flex min-[1200px]:gap-8">
              <Link to="/remake" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] whitespace-nowrap">
                AI BUILDER
              </Link>
              <Link to="/diamonds" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] whitespace-nowrap">
                DIAMONDS
              </Link>
              
              {/* CUSTOM Dropdown */}
              <div 
                className="relative group"
              >
                <button className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] flex items-center gap-1 whitespace-nowrap">
                  JEWELRY
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" strokeWidth={1.5} />
                </button>
                
                {/* Dropdown - stays open when hovering over it */}
                <div className="absolute top-full left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-[10000] py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  {categories.map((category) => (
                    <Link
                      key={category.path}
                      to={category.path}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              </div>
              
              <Link to="/portfolio" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] whitespace-nowrap">
                CATALOG
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] whitespace-nowrap">
                ABOUT
              </Link>
               <Link to="/book-consultation" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] whitespace-nowrap">
                CONSULTATION
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-[13px] lg:text-[15px] whitespace-nowrap">
                CONTACT
              </Link>
            </nav>

            {/* Actions - Desktop */}
            <div className="items-center hidden gap-0.5 min-[1200px]:flex flex-shrink-0">
              {/* Currency Selector */}
              <div className="hidden md:block">
                <CurrencySelector />
              </div>

              {/* <button className="p-2 transition-colors hover:text-blue-600" title="Search">
                <Search className="w-5 h-5 header-icon" strokeWidth={1.5} />
              </button> */}
              <a
                href={whatsappUrl}
                className="hidden p-1 transition-colors md:block hover:text-blue-600"
                title="Messages"
                target="_blank"
                rel="noreferrer"
              >
                <MessageCircle className="w-5 h-5 header-icon" strokeWidth={1.5} />
              </a>
              <Link to="/wishlist" className="relative hidden p-1 transition-colors sm:block hover:text-blue-600" title="Wishlist">
                <Heart className="w-5 h-5 header-icon" strokeWidth={1.5} />
                {wishlistItems.length > 0 && (
                  <span className="absolute px-1 text-xs text-white bg-[#455a6b] rounded-full -top-1 -right-1">
                    {wishlistItems.length}
                  </span>
                )}
              </Link>

              <button onClick={openDrawer} className="relative p-1 transition-colors hover:text-blue-600" title="Cart">
                <ShoppingBagIcon className="w-5 h-5 header-icon" strokeWidth={1.5} />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#455a6b] text-white text-xs rounded-full px-1">
                    {totalItems}
                  </span>
                )}
              </button>

              {user ? (
                <div className="relative group">
                  <button className="p-1 transition-colors hover:text-blue-600" title="Profile">
                    <User className="w-5 h-5 header-icon" strokeWidth={1.5} />
                  </button>
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[10000]">
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        My Profile
                      </Link>
                      <Link 
                        to="/orders" 
                        className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        My Orders
                      </Link>
                      <Link 
                        to="/addresses" 
                        className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                      >
                        Addresses
                      </Link>
                      <div className="pt-1 mt-1 border-t border-gray-100">
                        <button 
                          onClick={logout} 
                          className="block w-full px-4 py-2 text-sm text-left text-red-600 transition-colors hover:bg-red-50"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <Link to="/login" className="p-2 transition-colors hover:text-blue-600" title="Account">
                  <User className="w-5 h-5 header-icon" strokeWidth={1.5} />
                </Link>
              )}
            </div>

            {/* Actions - Mobile */}
            <div className="flex items-center gap-3 max-[659px]:flex min-[660px]:hidden">
              <button
                onClick={openDrawer}
                className="relative p-1 transition-colors hover:text-blue-600"
                title="Cart"
              >
                <ShoppingBagIcon className="w-5 h-5 header-icon" strokeWidth={1.5} />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#455a6b] text-white text-xs rounded-full px-1">
                    {totalItems}
                  </span>
                )}
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-1 transition-colors hover:text-blue-600"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
              </button>
            </div>
          </div>

          {/* Tablet Navigation (660px - 1199px) */}
          <div className="hidden min-[660px]:flex min-[1200px]:hidden flex-col items-center gap-3">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="SDNY Logo" className="w-auto h-11" />
            </Link>
            <nav className="flex flex-wrap items-center justify-center gap-5 text-[12px] text-gray-700">
              <Link to="/remake" className="font-medium transition-colors hover:text-blue-600">
                AI BUILDER
              </Link>
              <Link to="/products" className="font-medium transition-colors hover:text-blue-600">
                SHOP
              </Link>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setCustomDropdownOpen((prev) => !prev)}
                  className="flex items-center gap-1 font-medium transition-colors hover:text-blue-600"
                  aria-expanded={customDropdownOpen}
                >
                  JEWELRY
                  <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                {customDropdownOpen && (
                  <div className="absolute left-1/2 top-full z-[10000] mt-2 w-56 -translate-x-1/2 rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
                    {categories.map((category) => (
                      <Link
                        key={category.path}
                        to={category.path}
                        onClick={() => setCustomDropdownOpen(false)}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <Link to="/portfolio" className="font-medium transition-colors hover:text-blue-600">
                CATALOG
              </Link>
              <Link to="/about" className="font-medium transition-colors hover:text-blue-600">
                ABOUT
              </Link>
              <Link to="/book-consultation" className="font-medium transition-colors hover:text-blue-600">
                CONSULT
              </Link>
              <Link to="/contact" className="font-medium transition-colors hover:text-blue-600">
                CONTACT
              </Link>
            </nav>
            <div className="flex items-center justify-center gap-3 text-gray-700">
              <a href={whatsappUrl} title="Messages" target="_blank" rel="noreferrer">
                <MessageCircle className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <Link to="/wishlist" title="Wishlist">
                <Heart className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link to={user ? "/profile" : "/login"} title="Account">
                <User className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <button onClick={openDrawer} title="Cart">
                <ShoppingBagIcon className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={`fixed inset-0 z-[999] md:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          type="button"
          aria-label="Close menu"
          className="absolute inset-0 bg-black/30"
          onClick={() => setMobileMenuOpen(false)}
        />
        <div
          className={`absolute top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-lg transition-transform duration-300 ${
            mobileMenuOpen ? 'translate-y-0' : '-translate-y-6'
          }`}
        >
          <button
            type="button"
            aria-label="Close menu"
            className="absolute p-1 text-gray-700 top-4 right-4 hover:text-blue-600"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          <nav className="max-w-[1440px] mx-auto px-6 py-4 flex flex-col gap-4">
            <Link 
              to="/remake" 
              className="py-3 text-base font-medium text-gray-700 transition-colors border-b border-gray-100 hover:text-blue-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              AI BUILDER
            </Link>
            <Link 
              to="/diamonds" 
              className="py-3 text-base font-medium text-gray-700 transition-colors border-b border-gray-100 hover:text-blue-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              DIAMONDS
            </Link>
            
            {/* CUSTOM with expandable categories for mobile */}
            <div className="border-b border-gray-100">
              <button
                onClick={() => setCustomDropdownOpen(!customDropdownOpen)}
                className="flex items-center justify-between w-full py-3 text-base font-medium text-left text-gray-700 transition-colors hover:text-blue-600"
              >
                JEWELRY
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${customDropdownOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} />
              </button>
              {customDropdownOpen && (
                <div className="pb-3 pl-4 space-y-2">
                  {categories.map((category) => (
                    <Link
                      key={category.path}
                      to={category.path}
                      className="block py-2 text-sm text-gray-600 transition-colors hover:text-blue-600"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link 
              to="/about" 
              className="py-3 text-base font-medium text-gray-700 transition-colors border-b border-gray-100 hover:text-blue-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              ABOUT
            </Link>
            <Link 
              to="/portfolio" 
              className="py-3 text-base font-medium text-gray-700 transition-colors border-b border-gray-100 hover:text-blue-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              CATALOG
            </Link>
            <Link 
              to="/book-consultation" 
              className="py-3 text-base font-medium text-gray-700 transition-colors border-b border-gray-100 hover:text-blue-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              CONSULT
            </Link>
            <Link 
              to="/contact" 
              className="py-3 text-base font-medium text-gray-700 transition-colors hover:text-blue-600"
              onClick={() => setMobileMenuOpen(false)}
            >
              CONTACT
            </Link>
            
            {/* Mobile-only links */}
            <div className="flex flex-col gap-4 pt-4 border-t border-gray-200">
              {/* Currency Selector for Mobile */}
              <div className="flex flex-wrap items-center gap-3 py-2">
                <CurrencySelector />
                <Link
                  to="/wishlist"
                  className="relative p-1 transition-colors hover:text-blue-600"
                  title="Wishlist"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Heart className="w-5 h-5 header-icon" strokeWidth={1.5} />
                  {wishlistItems.length > 0 && (
                    <span className="absolute px-1 text-xs text-white bg-blue-600 rounded-full -top-1 -right-1">
                      {wishlistItems.length}
                    </span>
                  )}
                </Link>
                <a
                  href={whatsappUrl}
                  className="p-1 transition-colors hover:text-blue-600"
                  title="Messages"
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <MessageCircle className="w-5 h-5 header-icon" strokeWidth={1.5} />
                </a>
                {user ? (
                  <Link
                    to="/profile"
                    className="p-1 transition-colors hover:text-blue-600"
                    title="Profile"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="w-5 h-5 header-icon" strokeWidth={1.5} />
                  </Link>
                ) : (
                  <Link
                    to="/login"
                    className="p-1 transition-colors hover:text-blue-600"
                    title="Account"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="w-5 h-5 header-icon" strokeWidth={1.5} />
                  </Link>
                )}
              </div>
              
              <a
                href="mailto:info@sdnyusa.com"
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                aria-label="Email info@sdnyusa.com"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Mail className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} aria-hidden="true" />
                <span>info@sdnyusa.com</span>
              </a>
              <a
                href="tel:+16463021164"
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
                aria-label="Call +1 646 302 1164"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Phone className="w-5 h-5 flex-shrink-0" strokeWidth={1.8} aria-hidden="true" />
                <span>+1 646 302 1164</span>
              </a>
              <div className="flex items-center gap-4">
                <a
                  href="https://www.instagram.com/sdnyusa?igsh=MW5heWk1NzZxd291OQ=="
                  className="p-1 transition-opacity hover:opacity-70"
                  aria-label="Instagram sdnyusa"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <img src={instagram} alt="" className="w-5 h-5 brightness-0 opacity-70" />
                </a>
                <a
                  href="https://in.pinterest.com/sdnyusa/"
                  className="p-1 transition-opacity hover:opacity-70"
                  aria-label="Pinterest sdnyusa"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <img src={pinterest} alt="" className="w-5 h-5 brightness-0 opacity-70" />
                </a>
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Blue Info Banner */}
      {/* <div className="bg-[#4A90E2] text-white text-center py-2 text-sm font-medium">
        1440 Fill × 43 Hug */}
      {/* </div> */}
    </header>
  );
};

export default Header;
